import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, CheckCircle2, XCircle, ShieldCheck, ToggleLeft, ToggleRight } from "lucide-react";
import { getConfig, patchConfig, changeAdminRole, apiErr } from "../lib/api";
import { PageHead, Empty } from "./_ui";

const FLAG_LABELS = {
  puromitra: "PuroMitra AI assistant", proposals: "Proposals & booking loop",
  payments: "Cashfree payments", messages: "In-app messages",
};

export default function Configuration() {
  const [cfg, setCfg] = useState(null);
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => getConfig().then((d) => { setCfg(d); setS(d.settings); }).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setS({ ...s, [k]: e.target.value });
  const toggleFlag = (k) => setS({ ...s, feature_flags: { ...s.feature_flags, [k]: !s.feature_flags[k] } });

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        platform_fee_pct: Number(s.platform_fee_pct), business_name: s.business_name,
        support_phone: s.support_phone, support_email: s.support_email, gst_no: s.gst_no,
        service_city: s.service_city, feature_flags: s.feature_flags,
      };
      const d = await patchConfig(body);
      setCfg(d); setS(d.settings);
      toast.success("Configuration saved");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const onRole = async (id, role) => {
    try { await changeAdminRole(id, role); toast.success("Role updated"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (!s) return <div className="mx-auto max-w-[1400px] px-4 py-7 md:px-8"><Empty>Loading configuration…</Empty></div>;
  const inputCls = "mt-1 h-10 w-full rounded-lg border border-[#E5DFD3] px-3 font-body text-sm outline-none focus:border-[#E0A98F]";

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-7 md:px-8" data-testid="admin-configuration">
      <PageHead eyebrow="Platform" title="Configuration"
        desc="Business details, take rate, feature flags, live integration status and admin team roles."
        action={<button onClick={save} disabled={busy} data-testid="config-save" className="inline-flex items-center gap-1.5 rounded-lg bg-[#D84315] px-4 py-2.5 font-body text-sm font-semibold text-white hover:bg-[#BF360C] disabled:opacity-60"><Save size={16} /> {busy ? "Saving…" : "Save changes"}</button>} />

      <div className="mt-7 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Business + fee */}
        <div className="rounded-2xl border border-[#E9E2D6] bg-white p-6">
          <h2 className="font-heading text-lg font-semibold text-[#2C241B]">Business details</h2>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2"><label className="font-body text-sm font-medium text-[#2C241B]">Business name</label><input value={s.business_name} onChange={set("business_name")} data-testid="config-business-name" className={inputCls} /></div>
            <div><label className="font-body text-sm font-medium text-[#2C241B]">Support phone</label><input value={s.support_phone} onChange={set("support_phone")} className={inputCls} /></div>
            <div><label className="font-body text-sm font-medium text-[#2C241B]">Support email</label><input value={s.support_email} onChange={set("support_email")} className={inputCls} /></div>
            <div><label className="font-body text-sm font-medium text-[#2C241B]">Service city</label><input value={s.service_city} onChange={set("service_city")} className={inputCls} /></div>
            <div><label className="font-body text-sm font-medium text-[#2C241B]">GST number</label><input value={s.gst_no} onChange={set("gst_no")} className={inputCls} /></div>
            <div><label className="font-body text-sm font-medium text-[#2C241B]">Platform fee (%)</label><input type="number" value={s.platform_fee_pct} onChange={set("platform_fee_pct")} data-testid="config-fee" className={inputCls} /></div>
          </div>
        </div>

        {/* Feature flags */}
        <div className="rounded-2xl border border-[#E9E2D6] bg-white p-6">
          <h2 className="font-heading text-lg font-semibold text-[#2C241B]">Feature flags</h2>
          <p className="mt-1 font-body text-xs text-[#8A8072]">Turn marketplace capabilities on or off.</p>
          <div className="mt-4 space-y-2">
            {Object.keys(FLAG_LABELS).map((k) => (
              <button key={k} onClick={() => toggleFlag(k)} data-testid={`config-flag-${k}`}
                className="flex w-full items-center justify-between rounded-xl border border-[#F0EBE1] px-4 py-3 text-left hover:bg-[#FAF6EF]">
                <span className="font-body text-sm text-[#2C241B]">{FLAG_LABELS[k]}</span>
                {s.feature_flags?.[k] ? <ToggleRight size={26} className="text-[#6D8B44]" /> : <ToggleLeft size={26} className="text-[#C7BEAF]" />}
              </button>
            ))}
          </div>
        </div>

        {/* Integrations */}
        <div className="rounded-2xl border border-[#E9E2D6] bg-white p-6">
          <h2 className="font-heading text-lg font-semibold text-[#2C241B]">Integrations</h2>
          <p className="mt-1 font-body text-xs text-[#8A8072]">Live status of each service key in the backend environment.</p>
          <div className="mt-4 space-y-2">
            {(cfg?.integrations || []).map((i) => (
              <div key={i.env} data-testid={`config-integration-${i.env}`} className="flex items-center justify-between rounded-xl border border-[#F0EBE1] px-4 py-3">
                <div><p className="font-body text-sm font-medium text-[#2C241B]">{i.name}</p><p className="font-body text-[11px] text-[#A79C8A]">{i.env}</p></div>
                {i.connected
                  ? <span className="inline-flex items-center gap-1.5 rounded-full bg-[#E6F0EE] px-2.5 py-1 font-body text-[11px] font-semibold text-[#2F7A6B]"><CheckCircle2 size={13} /> Connected</span>
                  : <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FDF6EA] px-2.5 py-1 font-body text-[11px] font-semibold text-[#B98A2E]"><XCircle size={13} /> Not set</span>}
              </div>
            ))}
          </div>
        </div>

        {/* Admin team */}
        <div className="rounded-2xl border border-[#E9E2D6] bg-white p-6">
          <div className="flex items-center gap-2"><ShieldCheck size={18} className="text-[#D84315]" /><h2 className="font-heading text-lg font-semibold text-[#2C241B]">Admin team</h2></div>
          <div className="mt-4 space-y-2">
            {(cfg?.admins || []).map((a) => (
              <div key={a.id} data-testid={`config-admin-${a.id}`} className="flex items-center justify-between rounded-xl border border-[#F0EBE1] px-4 py-3">
                <div><p className="font-body text-sm font-medium text-[#2C241B]">{a.name || a.email}</p><p className="font-body text-[11px] text-[#A79C8A]">{a.email}</p></div>
                <select value={a.role} onChange={(e) => onRole(a.id, e.target.value)} data-testid={`config-role-${a.id}`}
                  className="h-9 rounded-lg border border-[#E5DFD3] px-2 font-body text-xs outline-none focus:border-[#E0A98F]">
                  <option value="super_admin">Super admin</option>
                  <option value="admin">Admin</option>
                  <option value="customer">Revoke (customer)</option>
                </select>
              </div>
            ))}
            {(cfg?.admins || []).length === 0 && <Empty>No admins found.</Empty>}
          </div>
        </div>
      </div>
    </div>
  );
}
